import { useState } from 'react';
import { T, useGT } from 'gt-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from 'src/components/ui/button';
import { Label } from 'src/components/ui/label';
import { Textarea } from 'src/components/ui/textarea';
import {
  useFreightOptionInstructions,
  type FreightOptionInstructionRow,
} from 'src/api/rpc/hooks/procurement/useFreightOptions';
import {
  CUSTOM,
  instructionChoices,
  NONE,
  wrapInstructionText,
} from './shippingInstructions';

/**
 * The shipping instructions printed on a PO (the header comment lines): one of
 * the freight option's saved instructions, none, or typed. Custom text is not
 * saved with the option; a refresh learns it from the release if it is used.
 * Remount it (key it by option) when the option changes.
 */
export function ShippingInstructionsField({
  idPrefix,
  optionId,
  value,
  onChange,
}: {
  idPrefix: string;
  optionId: string | null;
  value: string[];
  onChange: (lines: string[]) => void;
}) {
  const gt = useGT();
  const saved = useFreightOptionInstructions(optionId).data?.instructions ?? [];
  const [customText, setCustomText] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const { selected, listed, more } = instructionChoices({
    value,
    saved,
    typing: customText !== null,
    showAll,
  });

  function pick(next: string) {
    if (next === CUSTOM) {
      setCustomText(value.join('\n'));
      return;
    }
    setCustomText(null);
    onChange(
      next === NONE ? [] : (saved.find((row) => row.id === next)?.lines ?? []),
    );
  }

  return (
    <div className="space-y-1">
      <Label htmlFor={`${idPrefix}-instructions`}>
        <T context="PO freight field: header comment lines printed on the PO">
          Shipping instructions
        </T>
      </Label>
      <p className="text-muted-foreground text-xs">
        <T context="PO freight field hint: what shipping instructions are for">
          How to ship this order. Prints in the PO's header comments.
        </T>
      </p>
      <Select value={selected} onValueChange={(next) => pick(next ?? NONE)}>
        <SelectTrigger id={`${idPrefix}-instructions`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {listed.map((row) => (
            <SelectItem key={row.id} value={row.id}>
              {instructionLabel(gt, row)}
            </SelectItem>
          ))}
          <SelectItem value={NONE}>
            <T context="shipping instructions picker: print none">None</T>
          </SelectItem>
          <SelectItem value={CUSTOM}>
            <T context="shipping instructions picker: type your own">Custom</T>
          </SelectItem>
        </SelectContent>
      </Select>
      {more ? (
        <Button
          type="button"
          size="sm"
          variant="link"
          className="h-auto p-0 text-xs"
          onClick={() => setShowAll(true)}
        >
          <T context="shipping instructions picker: list every saved instruction">
            Show all saved instructions
          </T>
        </Button>
      ) : null}
      {selected === CUSTOM ? (
        <Textarea
          aria-label={gt('Custom shipping instructions', {
            context: 'shipping instructions picker: typed text',
          })}
          className="font-mono text-xs"
          rows={3}
          value={customText ?? value.join('\n')}
          onChange={(event) => {
            setCustomText(event.target.value);
            onChange(wrapInstructionText(event.target.value));
          }}
        />
      ) : null}
    </div>
  );
}

/** "SHIP VIA PUROLATOR 3707989 · default · 12×". */
function instructionLabel(
  gt: ReturnType<typeof useGT>,
  row: FreightOptionInstructionRow,
): string {
  return [
    row.lines.join(' '),
    row.isDefault
      ? gt('default', {
          context: "shipping instructions picker: the option's default",
        })
      : null,
    row.observedCount > 0
      ? gt('{count}×', {
          context: 'shipping instructions picker: times releases used it',
          count: String(row.observedCount),
        })
      : null,
  ]
    .filter(Boolean)
    .join(' · ');
}
